import React from 'react';
import pageHoc from 'hocs/pageHoc';
import { Box, Card, Flex, Text } from 'rebass/styled-components';
import { InfoBox } from 'components/atoms/InfoBox';
import { Button, Dropdown } from '@getro/rombo';
import { Plus, User, FileSpreadsheet } from 'lucide-react';
import { Link, NavLink } from 'react-router-dom';

import { MainLayoutHeader } from '../../components/atoms/mainLayoutHeader';
import MainLayout from '../../components/layout/Main';
import compose from '../../helpers/compose';
import { useNetworkOverview } from './hooks/useNetworkOverview';
import { Playbooks } from './playbooks';
import { useGreetings } from './hooks/useGreetings';

const pageTitle = 'Home';

const Home = () => {
  const {
    networkAdminsCount,
    alliesCount,
    isLoading,
    contactsCount,
    isLoadingContacts,
    activeNetwork,
    user,
    desktopWidth,
  } = useNetworkOverview();
  const greetings = useGreetings();

  return (
    <MainLayout>
      <MainLayoutHeader title={pageTitle} />
      <Flex flexDirection="column" mt="60px" mx="auto" px="16px" width="100%" maxWidth="850px" sx={{ gap: '80px' }}>
        <Flex sx={{ gap: '8px', flexDirection: 'column' }}>
          <Text fontSize="22px" fontWeight="700" lineHeight="120%" color="text.dark">
            👋 Hey {user?.firstName}
          </Text>
          <Text color="text.main" fontSize="16px" fontWeight="400" lineHeight="120%">
            {greetings}
          </Text>
        </Flex>
        <Flex sx={{ flexDirection: 'column', gap: '16px' }}>
          <Flex sx={{ gap: '8px', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Text color="text.dark" fontSize="16px" fontWeight="600" lineHeight="1.2">
              Network overview
            </Text>
            <Flex sx={{ gap: '8px', alignItems: 'center' }}>
              <Dropdown
                triggerProps={{
                  variant: 'tertiary',
                  size: 'small',
                  id: 'add-contacts-dropdown-trigger',
                  children: (
                    <Flex alignItems="center" fontSize="14px">
                      <Box as={Plus} mr="2" width="16px" height="16px" aria-hidden="true" />
                      Add contacts
                    </Flex>
                  ),
                  sx: {
                    height: '32px',
                  },
                }}
              >
                <Card px="0" py="8px" width="208px">
                  <Flex
                    as={NavLink}
                    to={`/networks/${activeNetwork.slug}/contact/add`}
                    height="32px"
                    px="16px"
                    sx={{ gap: '8px', alignItems: 'center', textDecoration: 'none', ':hover': { bg: 'neutral.50' } }}
                  >
                    <Box as={User} width="16px" height="16px" aria-hidden="true" />
                    Add single contact
                  </Flex>
                  <Flex
                    as={NavLink}
                    to={`/networks/${activeNetwork.slug}/import-contacts/csv`}
                    height="32px"
                    px="16px"
                    sx={{ gap: '8px', alignItems: 'center', textDecoration: 'none', ':hover': { bg: 'neutral.50' } }}
                  >
                    <Box as={FileSpreadsheet} width="16px" height="16px" aria-hidden="true" />
                    Import CSV
                  </Flex>
                </Card>
              </Dropdown>
              <Button
                size="small"
                as={Link}
                to={`/networks/${activeNetwork.slug}/settings/team-members?modal=open`}
                icon={Plus}
                iconGap="8px"
                variant="tertiary"
              >
                Invite team
              </Button>
            </Flex>
          </Flex>
          <Flex alignItems="strech" sx={{ gap: '16px', flexDirection: ['column', 'row'], mb: '16px' }}>
            <InfoBox
              width={['100%', desktopWidth]}
              title="Contacts"
              count={contactsCount}
              loading={isLoadingContacts}
              sx={{ height: 'auto' }}
              infoText={
                <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
                  <Text>
                    Total number of contacts in your network – from you, your team, allies, and your talent network.
                  </Text>
                  <Text>The more contacts you have, the more warm intros you can uncover!</Text>
                </Flex>
              }
            />
            <InfoBox
              width={['100%', desktopWidth]}
              title="Network admins"
              count={networkAdminsCount}
              loading={isLoading}
              sx={{ height: 'auto' }}
              infoText={
                <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
                  <Text>Team members who can manage your Getro network. </Text>
                  <Text>Admins can share their contacts, and help unlock value from your extended network. </Text>
                </Flex>
              }
            />
            <InfoBox
              title="Allies sharing their contacts"
              width={['100%', desktopWidth]}
              count={alliesCount}
              loading={isLoading}
              infoText={
                <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
                  <Text>
                    External collaborators (like investors, advisors, or founders) who are sharing their contacts with
                    your network.
                  </Text>
                  <Text>Invite trusted allies to uncover more warm intros!</Text>
                </Flex>
              }
              sx={{ height: 'auto' }}
            />
          </Flex>
        </Flex>
        {activeNetwork?.kind === 'ecosystem' && <Playbooks goals={activeNetwork?.userGoals} />}
      </Flex>
    </MainLayout>
  );
};

export default compose(pageHoc({ title: pageTitle }))(Home);
